// React and hooks
import React, { useState, useEffect, useRef } from 'react';

// Redux
import { useDispatch, useSelector } from 'react-redux';

// Routing
import { NavLink } from 'react-router-dom';

// UI Components (from React Bootstrap)
import { Row, Col, Dropdown, Form, Button, Alert } from 'react-bootstrap';

import moment from 'moment';
import { AnimatePresence, motion } from 'framer-motion';

import { handleCatchError, handleAPIError } from 'utils/handleErrors';
import { initialsUpperCase } from 'utils/textFormat';

// Custom Components
import HtmlHead from 'components/html-head/HtmlHead';

import CsLineIcons from 'cs-line-icons/CsLineIcons';
import EmptyPage from 'components/emptyStates/EmptyPage';
import EmptyFilterResult from 'components/emptyStates/EmptyFilterResult';
import ConnectionError from 'components/emptyStates/ConnectionError';
import Calendar from 'components/calendars/Calendar';
import LoadingPulse from '../../../components/loading/LoadingPulse';
import LoadingCar from '../../../components/loading/LoadingCar';
import CardShipping from './components/CardShipping';
import Paginate from '../../../components/Paginate';
import ShippingCSV from '../../../components/reactCsv/ShippingCSV';
import { ToastErrorApi, ToastErrorCath } from '../../../components/alerts/toast';
import ProvisionalUserAlert from '../../../components/alerts/UserAlert';
import ShippingRefundModal from '../../../components/modals/ShippingRefundModal';
import ReportModal from '../../../components/modals/ReportModal';

// Redux Actions
import {
  setPage,
  setItemsPerPage,
  setCarrier,
  setIdentifier,
  setInitialDate,
  setEndDate,
  setTrackingStatus,
  setFilteredMode,
  resetFilters,
} from '../../../slices/uiStates/userShippingsSlice';
// API calls
import { getShippingHistory, getFilteredHistory, getAllServices } from '../../../api/Api-user';

const { FormatMoney } = require('format-money-js');

const History = () => {
  const title = 'Historial';
  const description = 'Historial Plataforma Autopaquete';
  // - Use references to do scroll Up
  const startRef = useRef(null);
  const dispatch = useDispatch();
  // - Global State from Store.js
  const { page, itemsPerPage, carrier, identifier, trackingStatus, initialDate, endDate, filteredMode } = useSelector((state) => state.user_shippings);
  // -Local States
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoader, setIsInitialLoader] = useState(true);
  const [shippings, setShippings] = useState([]);
  const [emptyHistory, setEmptyHistory] = useState(null);
  const [hasConnectionIssue, setHasConnectionIssue] = useState(false);
  // Start Pagination States
  const [totalPages, setTotalPages] = useState(null);
  const [pageView, setPageView] = useState(page); // solo para la vista initial value from redux
  const [itemsPerPageView, setItemsPerPageView] = useState(itemsPerPage); // solo para la vista initial value from redux
  const [totalItems, setTotalItems] = useState(0);
  // End Pagination States
  // Start Filter States
  const [debouncedIdentifier, setDebouncedIdentifier] = useState(identifier);
  const [filteredSuccess, setFilteredSuccess] = useState('');
  const [servicesList, setServicesList] = useState([]);
  const [disabledFilters, setDisabledFilters] = useState({
    initialDate: false,
    endDate: false,
    carrier: false,
    tracking: false,
  });
  // End Filter States
  const [showActions, setShowActions] = useState(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [shippingToRefound, setShippingToRefound] = useState(false);
  const [updatePage, setUpdatePage] = useState(false);
  const alertVariants = {
    hidden: { x: 100, opacity: 0 }, // Aparece desde la derecha
    visible: { x: 0, opacity: 1 }, // Posición final (en pantalla)
    exit: { x: -100, opacity: 0 }, // Sale hacia la izquierda
  };
  const fm = new FormatMoney({
    decimals: 0, // No decimales
  });
  const AlertFilter = () => <em className="bg-foreground p-1 rounded-3 position-absolute bottom-50 end-0 text-small text-secondary">desactivado</em>;
  // Listener to filter by tracking state
  const handleFirstClick = (e, state) => {
    dispatch(setTrackingStatus({ view: e.target.innerText, request: state }));
  };
  // - Get all shippings
  const handlerGetShippingHistory = async () => {
    if (!isInitialLoader) {
      // solo si shipping es true [] activa este loader, si es falsy null usa el loader del car para los inicios
      setIsLoading(true);
    }
    try {
      const response = await getShippingHistory(page, itemsPerPage);
      // console.log('getShippingHistory response:', response);
      if (response.shippings !== undefined) {
        // en cada respuesta actualizar el estado currentPage
        // para asegurarse de que es la pag esperada
        setShippings(response.shippings);
        setTotalPages(response.total_pages); // para manejar la paginacion, si el valor es 1 no se muestra
        setTotalItems(response.total_items);
        setPageView(response.current_page); // para actualizar la vista
        setItemsPerPageView(response.page_size); // para actualizar la vista
        if (startRef.current && startRef.current.scrollIntoView) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      } else if (response.error) {
        setShippings(shippings || []); // si shippings es falsy actualiza con [] para eliminar el loader
        setTotalPages(0);
        setFilteredSuccess(''); // para eliminar el mensaje "Filtro por servicio..."
        const firstKey = Object.keys(response.error)[0];
        const errorMessage = response.error[firstKey][0].trim();
        if (!errorMessage.includes('No se encontraron envíos con los parámetros especificados.')) {
          // solo si es diferente a este error muestralo
          // es para que salga este mensaje cuando el historial esta vacio
          setEmptyHistory(false);
          ToastErrorApi(response.error[firstKey]);
        } else {
          setEmptyHistory(shippings || true); // para mostrar mensaje de "No tienes historial"
        }
      }
    } catch (error) {
      setShippings(shippings || []);
      handleCatchError(error);
      setHasConnectionIssue(true);
    } finally {
      setIsLoading(false);
      setIsInitialLoader(false);
    }
  };
  // - Search shipping by filters
  const handlerGetFilteredHistory = async () => {
    setEmptyHistory(false); // temporal para hacer pruebas
    if (!endDate && !initialDate && !identifier.trim() && !carrier.trim() && !trackingStatus.request) {
      dispatch(setFilteredMode(false)); // si changePage es mayor a 1 lo pone en true el btn buscar pero aqui es error esto es para que la paginacion siga funcionando en modo no filtrando
      ToastErrorCath(`Por favor, ingresa la información necesaria para filtrar.`);
      return;
    }
    try {
      //  setIsLoading(true);
      if (!isInitialLoader) {
        // solo si shipping es true [] activa este loader, si es falsy null usa el loader del car para los inicios
        setIsLoading(true);
      }
      // console.log('filtros:', page, itemsPerPage, identifier, carrier, initialDate, endDate, trackingStatus.request);
      const response = await getFilteredHistory(page, itemsPerPage, identifier, carrier, initialDate, endDate, trackingStatus.request);
      // console.log(' getFilteredHistory response:', response);
      if (response.shippings !== undefined) {
        setShippings(response.shippings);
        setTotalPages(response.total_pages);
        setPageView(response.current_page); // actualizar la vista de la paginacion
        setTotalItems(response.total_items);
        setItemsPerPageView(response.page_size); // para actualizar la vista
        setFilteredSuccess(
          <>
            Filtro
            {identifier && (
              <>
                {' '}
                Envío ▸ <strong>{initialsUpperCase(identifier)}</strong>
              </>
            )}
            {carrier && (
              <>
                {' '}
                Paquetería ▸ <strong>{initialsUpperCase(carrier)}</strong>
              </>
            )}
            {trackingStatus?.request && (
              <>
                {' '}
                Rastreo de envío ▸ <strong>{initialsUpperCase(trackingStatus.view)}</strong>
              </>
            )}
            {initialDate && (
              <>
                {' '}
                del <strong>{moment(initialDate).format('DD-MM-YY')}</strong>
              </>
            )}
            {endDate && (
              <>
                {' '}
                al <strong>{moment(endDate).format('DD-MM-YY')}</strong>
              </>
            )}
          </>
        );
        if (startRef.current && startRef.current.scrollIntoView) {
          startRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      } else if (response.error) {
        setShippings([]);
        handleAPIError(response);
        setTotalPages(0);
        setTotalItems(0);
        // setFilteredSuccess(''); // para eliminar el mensaje "Filtro por servicio..."
        setFilteredSuccess(true); // para mostrar el componente de emptyFilterResult"
      }
    } catch (error) {
      handleCatchError(error);
      // active state to show animation component error
      setHasConnectionIssue(true);
    } finally {
      setIsLoading(false);
      setIsInitialLoader(false);
      dispatch(setFilteredMode(true)); // respuesta exitosa o no es necesario para mantener el modo filtrando y que aparezca el btn regresar
    }
  };
  const settingPage = (record) => {
    dispatch(setItemsPerPage(record)); // ejecuta el useEffect que decide que funcion ejecutar
    dispatch(setPage(1)); // ejecuta el useEffect que decide que funcion ejecutar
  };
  const returnInitialMode = () => {
    // handlerGetFilteredHistory() o  handlerGetShippingHistory() activan el loader
    // setFilterBy('envío'); // ejecuta el useEffect que prepara los estados para el filtrado
    dispatch(setFilteredMode(false));
    setFilteredSuccess(''); // para eliminar el mensaje "Filtro por nombre..."
    dispatch(resetFilters());
    dispatch(setTrackingStatus({ view: '', request: '' }));
    if (page > 1) {
      dispatch(setPage(1)); // ejecutara el useEffect que decide que fn ejecutar pero con la pag 1
    } else {
      // si changePage es 1 no ejecuta el cambio de estado ni el useEffect es necesario llamar a la funcion
      handlerGetShippingHistory();
    }
  };
  // - Whenever the pagination status change
  useEffect(() => {
    if (filteredMode) {
      handlerGetFilteredHistory();
    } else {
      handlerGetShippingHistory();
    }
  }, [page, itemsPerPage, updatePage]);
  useEffect(() => {
    getAllServices()
      .then((response) => {
        // console.log('service list res:', response);
        setServicesList(response.list);
      })
      .catch(() => {
        setServicesList(['DHL', 'ESTAFETA', 'JYT', 'ESTAFETA']);
      });
  }, []);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedIdentifier(identifier);
    }, 500); // 500 ms de espera
    return () => clearTimeout(handler);
  }, [identifier]);
  useEffect(() => {
    if (debouncedIdentifier) {
      dispatch(setCarrier(''));
      dispatch(setInitialDate(''));
      dispatch(setEndDate(''));
      dispatch(setTrackingStatus({ view: '', request: '' }));
      setDisabledFilters({
        initialDate: true,
        endDate: true,
        carrier: true,
        tracking: true,
      });
    } else {
      setDisabledFilters({
        initialDate: false,
        endDate: false,
        carrier: false,
        tracking: false,
      });
    }
  }, [debouncedIdentifier]);
  return (
    <>
      {isInitialLoader && <LoadingCar />}
      {isLoading && <LoadingPulse />}
      <ShippingRefundModal
        setIsRefundModalOpen={setIsRefundModalOpen}
        isRefundModalOpen={isRefundModalOpen}
        shippingToRefound={shippingToRefound}
        setUpdatePage={setUpdatePage}
      />
      <ReportModal isModalOpen={isReportModalOpen} setIsModalOpen={setIsReportModalOpen} />
      {/* pageContainer I use it to add opacity screen while loading data */}
      <div id="pageContainer">
        <HtmlHead title={title} description={description} />
        <div className="page-title-container" ref={startRef}>
          <Row className="g-0">
            {/* Title Start */}
            <Col className="col-auto mb-3 mb-sm-0 me-auto order-1">
              <NavLink className="muted-link pb-1 d-inline-block hidden breadcrumb-back" to="/">
                <CsLineIcons icon="chevron-left" size="13" />
                <span className="align-middle text-small ms-1">Inicio</span>
              </NavLink>
              <h1 className="mb-0 pb-0 display-4" id="title">
                {title}
              </h1>
            </Col>
            {/* Title End */}
            {/* Excel Buttons Start */}
            <Col sm="auto" className="d-flex align-items-end justify-content-end mb-2 mb-sm-0 order-3 order-sm-2">
              {shippings?.length > 0 && <ShippingCSV shippings={shippings} crmId="" />}
            </Col>
            {/* Excel  Buttons End */}
            {/* Top Buttons Start */}
            <Col xs="auto" className="d-flex align-items-end justify-content-end mb-2 mb-sm-0 order-2 order-sm-3">
              <NavLink to="/envios/enviar" className="btn btn-icon btn-outline-primary btn-icon-start ms-0 ms-sm-2 w-100 w-md-auto">
                <CsLineIcons icon="plus" /> <span>Crear envío</span>
              </NavLink>
            </Col>
            {/* Top Buttons End */}
          </Row>
        </div>
        <div style={{ minHeight: '50vh' }} id="cardContainer">
          {/* Elements for Filtering Start */}
          <Row className="g-0 mt-sm-5 d-flex align-items-end bg-infox">
            {/* Filters Start */}
            <Col xs="12" sm="auto" xl="10" className="bg-warningx">
              <Row className="g-0">
                {/* Inputs filters Start */}
                <AnimatePresence>
                  <motion.div
                    // className="bg-info"
                    key="filter-message-wrapper"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: disabledFilters.initialDate ? 1 : 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{ height: '1.5rem' }} // asegura altura base para evitar saltos
                  >
                    {disabledFilters.initialDate && (
                      <motion.em
                        className="text-medium text-info"
                        initial={false}
                        animate={{
                          opacity: disabledFilters.initialDate ? 1 : 0,
                          y: disabledFilters.initialDate ? 0 : -4,
                        }}
                        transition={{
                          duration: 0.3,
                          delay: disabledFilters.initialDate ? 0 : 2, // ← retrasa solo al desaparecer
                        }}
                        style={{ display: 'block', minHeight: '1.5rem' }} // evita el salto
                      >
                        * Al filtrar por ID de envío o nombre del destinatario, no es posible combinarlo con otros filtros *
                      </motion.em>
                    )}
                  </motion.div>
                </AnimatePresence>
                <Col xs="12" sm="auto" className="position-relative mb-2 me-2 order-2 order-lg-1 bg-successx">
                  <Dropdown className="d-inline-block w-100">
                    <Dropdown.Toggle
                      disabled={disabledFilters.tracking}
                      variant="foreground-alternate"
                      className="shadow d-flex align-items-center justify-content-between w-100"
                      style={{ fontFamily: 'Montserrat', minWidth: '250px' }}
                    >
                      {trackingStatus.view || 'Rastreo'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="shadow sw-auto" style={{ minHeight: '100px' }}>
                      {trackingStatus.view && <Dropdown.Item onClick={() => dispatch(setTrackingStatus({ view: '', request: '' }))}>NINGUNO</Dropdown.Item>}
                      <Dropdown.Item onClick={(e) => handleFirstClick(e, 'CREATED_LABEL')}>ETIQUETA CREADA</Dropdown.Item>
                      <Dropdown.Item onClick={(e) => handleFirstClick(e, 'ON_THE_WAY')}>EN CAMINO</Dropdown.Item>
                      <Dropdown.Item onClick={(e) => handleFirstClick(e, 'PROCESS_DELIVERY')}>PROCESO DE ENTREGA</Dropdown.Item>
                      <Dropdown.Item onClick={(e) => handleFirstClick(e, 'DELIVERED')}>ENTREGADO</Dropdown.Item>
                      <Dropdown.Item onClick={(e) => handleFirstClick(e, 'EXCEPTION')}>EXCEPCIÓN</Dropdown.Item>
                      <Dropdown.Item onClick={(e) => handleFirstClick(e, 'RETURNED')}>RETORNADO</Dropdown.Item>
                      <Dropdown.Item onClick={(e) => handleFirstClick(e, 'UPDATED_DELIVERY')}>ENTREGA ACTUALIZADA</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  {disabledFilters.tracking && <AlertFilter />}
                </Col>
                <Col xs="12" sm="auto" className="position-relative mb-2 me-2 order-3 order-md-2 bg-alternatex">
                  <Dropdown className="d-inline-block w-100">
                    <Dropdown.Toggle
                      disabled={disabledFilters.carrier}
                      variant="foreground-alternate"
                      className="shadow d-flex align-items-center justify-content-between w-100"
                      style={{ fontFamily: 'Montserrat', minWidth: '200px' }}
                    >
                      {carrier || 'Paqueteria'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="shadow sw-auto" style={{ minHeight: '100px' }}>
                      {carrier && <Dropdown.Item onClick={() => dispatch(setCarrier(''))}>NINGUNA</Dropdown.Item>}
                      {servicesList?.map((service) => (
                        <Dropdown.Item key={service} onClick={() => dispatch(setCarrier(service))}>
                          {service}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  {disabledFilters.carrier && <AlertFilter />}
                </Col>
                <Col lg="5" className="d-none d-lg-block bg-secondaryx order-3" />
                <Col xs="12" sm="8" md="auto" className="mb-2 mb-xl-0 me-2 order-1 order-lg-4">
                  <Form.Control
                    type="text"
                    style={{ fontFamily: 'Montserrat', minWidth: '275px' }}
                    placeholder="ID de envío o Nombre del destinatario"
                    value={identifier}
                    onChange={(e) => {
                      dispatch(setIdentifier(e.target.value));
                    }}
                  />
                </Col>
                {/* Inputs filters End */}
                {/* Calendars Start */}
                <Col xs="12" sm="auto" className="mb-2 order-5">
                  <Row className="g-0 me-sm-2">
                    <Col xs="12" sm="6" className="position-relative pe-sm-2 mb-2 mb-sm-0">
                      <Calendar
                        isDisabled={disabledFilters.initialDate}
                        selectDate={initialDate}
                        setState={setInitialDate}
                        holder="Fecha inicial"
                        width="100px"
                      />
                      {disabledFilters.initialDate && <AlertFilter />}
                    </Col>
                    <Col xs="12" sm="6" className="position-relative ">
                      <Calendar isDisabled={disabledFilters.initialDate} selectDate={endDate} setState={setEndDate} holder="Fecha final" width="100px" />
                      {disabledFilters.endDate && <AlertFilter />}
                    </Col>
                  </Row>
                </Col>
                {/* Calendars End */}
                <Col xs="12" xl="auto" className="position-relative order-5 bg-infox">
                  <Row className="g-0">
                    <Col xs="auto" className="pe-2 ">
                      <Button
                        variant="primary"
                        style={{ height: '36px' }}
                        className="w-100"
                        onClick={() => {
                          if (page > 1) {
                            // la primera vez el estado filteredMode es falso
                            dispatch(setFilteredMode(true)); // para que el useEffect funcione bien
                            dispatch(setPage(1)); // ejecutara el useEffect que decide que fn ejecutar pero con la pag 1
                          } else {
                            handlerGetFilteredHistory();
                          }
                        }}
                      >
                        <span>Buscar</span>
                      </Button>
                    </Col>
                    {filteredMode && (
                      <Col xs="auto">
                        <Button variant="outline-primary" style={{ height: '36px' }} className="w-100" onClick={returnInitialMode}>
                          <span>Regresar</span>
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row>
            </Col>
            {/* Filters End */}
            {/* Items selector Start */}
            <Col xs="12" xl="2" className="mt-2 mb-lg-2 mt-lg-0 d-flex justify-content-end align-items-end bg-secondaryx">
              <Row className="g-0">
                <Col>
                  <Dropdown align={{ xs: 'end' }} className={`d-inline-block ms-1 ${shippings?.length < 2 && 'd-none'}`}>
                    <Dropdown.Toggle variant="foreground-alternate" className="shadow sw-15">
                      {`${itemsPerPageView} Envíos`}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="shadow dropdown-menu-end">
                      <Dropdown.Item className={`${itemsPerPageView === 10 && 'd-none'}`} onClick={() => settingPage(10)}>
                        10 Envíos
                      </Dropdown.Item>
                      <Dropdown.Item className={`${itemsPerPageView === 20 && 'd-none'}`} onClick={() => settingPage(20)}>
                        20 Envíos
                      </Dropdown.Item>
                      <Dropdown.Item className={`${itemsPerPageView === 50 && 'd-none'}`} onClick={() => settingPage(50)}>
                        50 Envíos
                      </Dropdown.Item>
                      <Dropdown.Item className={`${itemsPerPageView === 100 && 'd-none'}`} onClick={() => settingPage(100)}>
                        100 Envíos
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              </Row>
            </Col>
            {/* Items selector End */}
          </Row>
          {/* Elements for Filtering End */}
          <hr className="bg-muted mb-3 mt-3 mt-xl-4" />
          <AnimatePresence>
            <motion.div key="custom-alert" initial="hidden" animate="visible" exit="exit" variants={alertVariants} transition={{ duration: 0.4 }}>
              <Alert className="mb-2 d-flex align-items-center" variant="info">
                <div>
                  <strong>
                    <span role="img" aria-label="nuevo" className="me-2">
                      ✨
                    </span>
                    ¡Nuevo!
                  </strong>{' '}
                  Ahora puedes gestionar reportes, recolecciones y reembolsos directamente desde tu historial de envíos.
                  <br />
                  <strong>Haz clic en el botón con el ícono de (...) que aparece en cada tarjeta de envío para acceder a estas opciones.</strong>
                </div>
              </Alert>
            </motion.div>
          </AnimatePresence>
          {/* List Header Start */}
          {filteredSuccess && <div className="text-muted mb-3">{filteredSuccess}</div>}
          <div className="d-flex justify-content-between mb-3 text-muted">
            <span>
              Total: <strong>{fm.from(totalItems)}</strong> {`envíos ${!filteredMode ? '' : 'encontrados'} `}
            </span>
            <span className={`${shippings?.length < 2 && 'd-none'}`}>
              Página <strong>{pageView}</strong>
            </span>
          </div>
          <ProvisionalUserAlert title="Historial de envíos" />
          <Row className={`g-0 mb-2 d-none d-xl-flex ${shippings?.length === 0 && 'invisible'} `}>
            <Col xs="auto" className="sw-11 d-none d-xl-flex" />
            <Col>
              <Row className="g-0 h-100 align-content-center custom-sort ps-5 pe-4 h-100">
                <Col xs="3" className="d-flex flex-column mb-xl-0 pe-3 d-flex">
                  <div className="text-muted text-small cursor-pointer">SERVICIO / ID ENVÍO</div>
                </Col>
                <Col xs="2" xl="2" className="d-flex flex-column pe-1 justify-content-center">
                  <div className="text-muted text-small cursor-pointer">FECHA / HORA</div>
                </Col>
                <Col xs="2" xl="2" className="d-flex flex-column pe-1 justify-content-center" />
                <Col xs="2" xl="2" className="d-flex flex-column pe-1 justify-content-center align-items-center">
                  <div className="text-muted text-small cursor-pointer">COSTO</div>
                </Col>
                <Col xs="2" xl="1" className="d-flex flex-column pe-1 justify-content-center align-items-center">
                  <div className="text-muted text-small cursor-pointer">PESO COBRADO</div>
                </Col>
                <Col xs="2" xl="1" className="d-flex flex-column pe-1 justify-content-center align-items-center" />
              </Row>
            </Col>
          </Row>
          {/* List Header End */}
          {/* List shipment Items End */}
          {shippings?.length === 0 && emptyHistory && (
            <EmptyPage
              text="Tu historial de envíos está vacío. Una vez que comiences a generar envíos, aquí podrás consultar el rastreo, los detalles de cada paquete y aplicar filtros por fechas, paquetería o número de guía."
              btnText="Crear envío"
              path="/envios/enviar"
              isCallToAction
              showAnimation
            />
          )}
          {shippings?.length === 0 && filteredSuccess && <EmptyFilterResult />}
          {shippings?.length > 0 && (
            <>
              {/* List shipment Items Start */}
              {shippings?.map((shipping) => (
                <CardShipping
                  key={shipping.shipping_label_id}
                  shipping={shipping}
                  showActions={showActions}
                  setShowActions={setShowActions}
                  setIsReportModalOpen={setIsReportModalOpen}
                  setIsRefundModalOpen={setIsRefundModalOpen}
                  setShippingToRefound={setShippingToRefound}
                />
              ))}
              {/* List shipment Items End */}
              {/* Pagination Start */}
              <div className={`d-flex flex-column justify-content-center align-items-center mt-5 mx-auto ${totalPages < 2 && 'd-none'}`}>
                <Paginate currentPage={page} totalPages={totalPages} setPageReducer={setPage} />
                {/* se elimino el prop setIsLoaging */}
              </div>
              {/* Pagination End */}
            </>
          )}
          {shippings?.length === 0 && hasConnectionIssue && <ConnectionError />}
        </div>
      </div>
    </>
  );
};

export default History;
// onClick={(e) => nextPage(e.currentTarget)}
